import React from 'react';
import { Card, CardContent, CardFooter } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, Instagram, CheckCircle2, Info } from 'lucide-react';
import { isOfferInStock } from '../data/offers';

export const OfferCard = ({ offer, onViewDetails, featured = false }) => {
  const inStock = isOfferInStock(offer);

  const handleBuyClick = () => {
    window.open(offer.ctaInstagramUrl, '_blank');
  };

  return (
    <Card
      className={`glass-card group overflow-hidden ${
        inStock ? 'hover-glow' : 'opacity-85 ring-1 ring-warning/20'
      } ${featured ? 'border-primary/50 shadow-premium' : ''}`}
    >
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="text-4xl">{offer.logo}</div>
            <div>
              <h3 className="text-xl font-bold text-foreground">{offer.serviceName}</h3>
              <p className="text-sm text-muted-foreground">{offer.planName}</p>
            </div>
          </div>

          {!inStock ? (
            <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">
              <Clock className="w-3 h-3 mr-1" />
              Coming soon
            </Badge>
          ) : (
            offer.badge && (
              <Badge className="shimmer text-xs font-semibold">
                {offer.badge}
              </Badge>
            )
          )}
        </div>

        {/* Discount Badge / Stock */}
        <div className="flex items-baseline gap-2">
          {inStock ? (
            <div className="inline-flex items-center gap-2 bg-success/20 text-success px-3 py-1 rounded-full">
              <span className="text-2xl font-bold">{offer.discountPercent}%</span>
              <span className="text-xs font-medium uppercase">OFF</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-warning/15 text-warning px-3 py-1 rounded-full">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-semibold">Out of stock</span>
            </div>
          )}
        </div>

        {/* Pricing */}
        {inStock ? (
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-foreground">
                {offer.currencySymbol}{offer.finalPrice.toFixed(2)}
              </span>
              <span className="text-lg text-muted-foreground line-through">
                {offer.currencySymbol}{offer.originalPrice.toFixed(2)}
              </span>
              <span className="text-sm text-muted-foreground">{offer.billingPeriod}</span>
            </div>
            <p className="text-xs text-accent font-medium flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Pay after activation
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">Price hidden</span>
              <span className="text-sm text-muted-foreground">until restock</span>
            </div>
            <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Restocking soon
            </p>
          </div>
        )}

        {/* Highlights */}
        <div className="space-y-2 pt-2">
          {offer.highlights.slice(0, 4).map((highlight, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{highlight}</span>
            </div>
          ))}
        </div>

        {/* Delivery Note */}
        <div className="pt-2 border-t border-border/50">
          <p className="text-xs text-muted-foreground italic">
            {inStock ? offer.deliveryNote : 'Currently out of stock - restocking soon'}
          </p>
        </div>
      </CardContent>

      <CardFooter className="p-6 pt-0 flex flex-col gap-3">
        <Button
          variant={inStock ? 'default' : 'outline'}
          className={`w-full shadow-elegant font-semibold group-hover:shadow-glow transition-all ${
            inStock
              ? 'bg-primary hover:bg-primary-glow text-primary-foreground'
              : 'border-warning/30 bg-warning/10 text-warning hover:bg-warning/15 hover:text-warning'
          }`}
          onClick={handleBuyClick}
        >
          <Instagram className="w-4 h-4 mr-2" />
          {inStock ? 'Buy via Instagram' : 'Notify on Instagram'}
        </Button>
        <button
          onClick={() => onViewDetails(offer)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
        >
          <Info className="w-3 h-3" />
          View details
        </button>
      </CardFooter>
    </Card>
  );
};
